import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HardHat, Eye, EyeSlash, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1759922378219-1d31edb644f4?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

const RECURSOS = [
  "Acompanhe seus contratos e saldos",
  "Elabore e envie medições de obras",
  "Anexe relatórios fotográficos e documentos",
  "Acompanhe a análise da fiscalização",
];

export default function FornecedorLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState("");
  const [modo, setModo] = useState("login");
  const [enviado, setEnviado] = useState("");

  const solicitar = async (e) => {
    e.preventDefault();
    setBusy(true); setErro(""); setEnviado("");
    try {
      const { data } = await api.post("/auth/fornecedor/esqueci-senha", { email });
      setEnviado(data.message);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setErro(typeof d === "string" ? d : "Informe um e-mail válido.");
    } finally { setBusy(false); }
  };

  if (user?.role === "fornecedor") return <Navigate to="/fornecedor" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErro("");
    try {
      await login(email, password, "fornecedor");
      toast.success("Acesso autorizado");
      nav("/fornecedor");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setErro(typeof d === "string" ? d : "Não foi possível entrar. Tente novamente.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex bg-[#0B1220] text-[#E9EEF5]" data-testid="fornecedor-login-page" translate="no">
      <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-[#002776]" data-testid="forn-login-hero">
        <img src={HERO_IMG} alt="Engenheiro em campo acompanhando obra pública" data-testid="forn-login-hero-img"
             className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#001338]/95 via-[#002776]/75 to-[#002776]/55" />
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#009C3B] via-[#FFDF00] to-[#009C3B]" />
        <div className="relative z-10 flex flex-col justify-between p-14 w-full">
          <div className="flex items-center gap-3">
            <HardHat weight="duotone" size={36} className="text-[#FFDF00]" />
            <div>
              <div className="font-heading text-xl tracking-tight">Portal do Fornecedor</div>
              <div className="uppercase-label !text-[#FFDF00]/80">Soluções Contratuais</div>
            </div>
          </div>
          <div>
            <div className="uppercase-label mb-3 !text-[#FFDF00]">Empresas contratadas</div>
            <h1 className="font-heading text-5xl lg:text-6xl tracking-tight leading-tight">Medições e contratos<br />em um só lugar.</h1>
            <ul className="mt-8 space-y-3">
              {RECURSOS.map((r) => (
                <li key={r} className="flex items-center gap-3 text-[#CBD5E1]"><CheckCircle size={18} weight="fill" className="text-[#009C3B]" />{r}</li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-[#94A3B8]">O acesso é concedido pela administração contratante. Cada empresa visualiza apenas os próprios contratos.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden mb-10 flex items-center gap-3">
            <HardHat weight="duotone" size={32} className="text-[#FFDF00]" />
            <div className="font-heading text-lg">Portal do Fornecedor</div>
          </div>
          {modo === "esqueci" ? (
            <div data-testid="forn-esqueci-form">
              <div className="uppercase-label mb-2 !text-[#009C3B]">Recuperar acesso</div>
              <h2 className="font-heading text-3xl tracking-tight mb-2">Esqueci minha senha</h2>
              <p className="text-[#94A3B8] text-sm mb-8">Informe o e-mail cadastrado. Enviaremos um link válido por 1 hora para criar uma nova senha.</p>
              <form onSubmit={solicitar} className="space-y-5">
                <input data-testid="forn-esqueci-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       className="tce-input w-full px-4 py-3 rounded-md" placeholder="contato@empresa.com.br" />
                {enviado && <div data-testid="forn-esqueci-ok" className="text-sm text-[#22C55E] bg-[#22C55E]/10 border border-[#22C55E]/40 rounded-md px-3 py-2">{enviado}</div>}
                {erro && <div data-testid="forn-esqueci-erro" className="text-sm text-[#EF4444]">{erro}</div>}
                <button data-testid="forn-esqueci-submit" disabled={busy} className="w-full bg-[#009C3B] text-white font-medium py-3 rounded-md hover:bg-[#00B347] disabled:opacity-50">
                  {busy ? "Enviando…" : "Enviar link de redefinição"}
                </button>
              </form>
              <button data-testid="forn-voltar-login" onClick={() => { setModo("login"); setErro(""); setEnviado(""); }} className="mt-6 text-sm text-[#94A3B8] hover:text-[#FFDF00]">← Voltar ao login</button>
            </div>
          ) : (<>
          <div className="uppercase-label mb-2 !text-[#009C3B]">Acesso da empresa contratada</div>
          <h2 className="font-heading text-3xl tracking-tight mb-2">Entrar no portal</h2>
          <p className="text-[#94A3B8] text-sm mb-10">Use o e-mail e a senha fornecidos pela administração.</p>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="uppercase-label block mb-2">E-mail</label>
              <input data-testid="forn-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     className="tce-input w-full px-4 py-3 rounded-md" placeholder="contato@empresa.com.br" />
            </div>
            <div>
              <label className="uppercase-label block mb-2">Senha</label>
              <div className="relative">
                <input data-testid="forn-login-password" type={showPw ? "text" : "password"} required value={password}
                       onChange={(e) => setPassword(e.target.value)} className="tce-input w-full px-4 py-3 rounded-md pr-12" placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="forn-toggle-password"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#FFDF00]">
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {erro && <div data-testid="forn-login-error" className="text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-md px-3 py-2">{erro}</div>}
            <button data-testid="forn-login-submit" disabled={busy}
                    className="w-full bg-[#009C3B] text-white font-medium py-3 rounded-md hover:bg-[#00B347] transition-colors disabled:opacity-50">
              {busy ? "Entrando…" : "Entrar no Portal"}
            </button>
          </form>
          <button data-testid="forn-esqueci-link" onClick={() => { setModo("esqueci"); setErro(""); }} className="mt-4 text-sm text-[#009C3B] hover:text-[#22C55E]">Esqueci minha senha</button>
          </>)}
          <div className="mt-8 pt-6 border-t border-[#334155] text-sm text-[#94A3B8]">
            <a href="/login" data-testid="link-login-interno" className="inline-flex items-center gap-2 hover:text-[#FFDF00]">
              <ArrowLeft size={14} /> Sou servidor (fiscal ou gestor) — acessar o sistema interno
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
